import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Alert, Card } from 'react-bootstrap';
import aiService from '../services/aiService';
import authService from '../services/authService';
import businessService from '../services/businessService';
import targetMarketService from '../services/targetMarketService';
import BackToDashboard from '../components/BackToDashboard';

function MissionVisionRefinement() {
  const navigate = useNavigate();
  const [stage, setStage] = useState('choice'); // choice, mission, vision, both, complete
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Original statements
  const [originalMission, setOriginalMission] = useState('');
  const [originalVision, setOriginalVision] = useState('');
  const [targetMarket, setTargetMarket] = useState('');

  // Mission refinement
  const [missionOptions, setMissionOptions] = useState([]);
  const [selectedMissionOption, setSelectedMissionOption] = useState(null);
  const [editedMission, setEditedMission] = useState('');

  // Vision refinement
  const [visionOptions, setVisionOptions] = useState([]);
  const [selectedVisionOption, setSelectedVisionOption] = useState(null);
  const [editedVision, setEditedVision] = useState('');

  useEffect(() => {
    checkAuthAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthAndLoad = async () => {
    // Check authentication
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Check if target market is completed
    const targetResult = await targetMarketService.getPrimaryNiche();
    if (!targetResult.success || !targetResult.data.primary_niche) {
      setError('Please complete Target Market Discovery first');
      return;
    }

    setTargetMarket(targetResult.data.primary_niche.niche_name);

    // Load current mission and vision statements
    const fieldsResult = await businessService.getFieldsByNames([
      'mission_statement', 'vision_statement'
    ]);
    if (fieldsResult.success && fieldsResult.data.fields) {
      setOriginalMission(fieldsResult.data.fields.mission_statement || '');
      setOriginalVision(fieldsResult.data.fields.vision_statement || '');
    }
  };

  const handleChoice = async (choice) => {
    if (choice === 'skip') {
      navigate('/dashboard/foundation');
      return;
    }

    setLoading(true);
    setError('');

    if (choice === 'mission' || choice === 'both') {
      // Generate mission refinements
      const result = await aiService.refineMission();
      setLoading(false);

      if (result.success) {
        setMissionOptions(result.data.options);
        setStage(choice === 'both' ? 'both_mission' : 'mission');
      } else {
        setError(result.error);
      }
    } else if (choice === 'vision') {
      // Generate vision refinements
      const result = await aiService.refineVision();
      setLoading(false);

      if (result.success) {
        setVisionOptions(result.data.options);
        setStage('vision');
      } else {
        setError(result.error);
      }
    }
  };

  const selectMissionOption = (index) => {
    setSelectedMissionOption(index);
    setEditedMission(missionOptions[index]);
  };

  const selectVisionOption = (index) => {
    setSelectedVisionOption(index);
    setEditedVision(visionOptions[index]);
  };

  const saveMission = async () => {
    if (!editedMission.trim()) {
      setError('Please select or enter a mission statement');
      return;
    }

    setLoading(true);
    setError('');

    const result = await aiService.saveMission({
      mission_statement: editedMission
    });

    setLoading(false);

    if (result.success) {
      if (stage === 'both_mission') {
        // Continue to vision refinement
        setLoading(true);
        const visionResult = await aiService.refineVision();
        setLoading(false);

        if (visionResult.success) {
          setVisionOptions(visionResult.data.options);
          setStage('both_vision');
        } else {
          setError(visionResult.error);
        }
      } else {
        // Done with mission only
        setStage('complete');
      }
    } else {
      setError(result.error);
    }
  };

  const saveVision = async () => {
    if (!editedVision.trim()) {
      setError('Please select or enter a vision statement');
      return;
    }

    setLoading(true);
    setError('');

    const result = await aiService.saveVision({
      vision_statement: editedVision
    });

    setLoading(false);

    if (result.success) {
      setStage('complete');
    } else {
      setError(result.error);
    }
  };

  const regenerateMission = async () => {
    setSelectedMissionOption(null);
    setEditedMission('');
    setLoading(true);
    setError('');

    const result = await aiService.refineMission();
    setLoading(false);

    if (result.success) {
      setMissionOptions(result.data.options);
    } else {
      setError(result.error);
    }
  };

  const regenerateVision = async () => {
    setSelectedVisionOption(null);
    setEditedVision('');
    setLoading(true);
    setError('');

    const result = await aiService.refineVision();
    setLoading(false);

    if (result.success) {
      setVisionOptions(result.data.options);
    } else {
      setError(result.error);
    }
  };

  const renderChoice = () => (
    <Card className="shadow">
      <Card.Body className="p-4">
        <h2 className="mb-4">Mission & Vision Refinement</h2>

        <p className="text-muted mb-4">
          Now that you've selected your primary target market (<strong>{targetMarket}</strong>),
          you have the option to refine your mission and vision statements to be specifically
          tailored to this audience.
        </p>

        {error && <Alert variant="danger">{error}</Alert>}

        <Alert variant="info">
          <strong>Your Current Statements:</strong>
          <div className="mt-3">
            <strong>Mission:</strong> {originalMission}
          </div>
          <div className="mt-2">
            <strong>Vision:</strong> {originalVision}
          </div>
        </Alert>

        <p className="mb-4">
          Would you like to refine these statements for your target market?
        </p>

        <div className="d-grid gap-2">
          <Button
            variant="outline-secondary"
            onClick={() => handleChoice('skip')}
            disabled={loading}
          >
            Skip - Keep Current Statements
          </Button>
          <Button
            variant="primary"
            onClick={() => handleChoice('mission')}
            disabled={loading}
          >
            {loading ? 'Generating...' : 'Refine Mission Only'}
          </Button>
          <Button
            variant="primary"
            onClick={() => handleChoice('vision')}
            disabled={loading}
          >
            {loading ? 'Generating...' : 'Refine Vision Only'}
          </Button>
          <Button
            variant="success"
            onClick={() => handleChoice('both')}
            disabled={loading}
          >
            {loading ? 'Generating...' : 'Refine Both (Recommended)'}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );

  const renderMission = () => (
    <Card className="shadow">
      <Card.Body className="p-4">
        <h2 className="mb-4">Refined Mission Statements</h2>

        <p className="text-muted mb-4">
          These options are tailored specifically for <strong>{targetMarket}</strong>.
          Select the one that best represents your mission, or customize it.
        </p>

        {error && <Alert variant="danger">{error}</Alert>}

        <Alert variant="info">
          <strong>Original Mission:</strong> {originalMission}
        </Alert>

        {missionOptions.map((option, index) => (
          <Card
            key={index}
            className={`mb-3 ${selectedMissionOption === index ? 'border-primary' : ''}`}
            style={{ cursor: 'pointer' }}
            onClick={() => selectMissionOption(index)}
          >
            <Card.Body>
              <Form.Check
                type="radio"
                name="mission"
                id={`mission-option-${index}`}
                checked={selectedMissionOption === index}
                onChange={() => selectMissionOption(index)}
                label={
                  <div>
                    <h5 className="mb-2">Option {index + 1}</h5>
                    <p className="mb-0">{option}</p>
                  </div>
                }
              />
            </Card.Body>
          </Card>
        ))}

        {selectedMissionOption !== null && (
          <Card className="mb-3 bg-light">
            <Card.Body>
              <Form.Label><strong>Customize (Optional):</strong></Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={editedMission}
                onChange={(e) => setEditedMission(e.target.value)}
              />
            </Card.Body>
          </Card>
        )}

        <div className="text-center mb-3">
          <Button
            variant="link"
            onClick={regenerateMission}
            disabled={loading}
          >
            🔄 Regenerate Options
          </Button>
        </div>

        <div className="d-flex justify-content-between">
          <Button
            variant="outline-secondary"
            onClick={() => navigate('/dashboard/foundation')}
          >
            Save for Later
          </Button>
          <Button
            variant="primary"
            onClick={saveMission}
            disabled={loading || selectedMissionOption === null}
          >
            {loading ? 'Saving...' : (stage === 'both_mission' ? 'Save & Continue to Vision' : 'Save Mission Statement')}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );

  const renderVision = () => (
    <Card className="shadow">
      <Card.Body className="p-4">
        <h2 className="mb-4">Refined Vision Statements</h2>

        <p className="text-muted mb-4">
          These options are tailored specifically for <strong>{targetMarket}</strong>.
          Select the one that best represents your vision, or customize it.
        </p>

        {error && <Alert variant="danger">{error}</Alert>}

        <Alert variant="info">
          <strong>Original Vision:</strong> {originalVision}
        </Alert>

        {visionOptions.map((option, index) => (
          <Card
            key={index}
            className={`mb-3 ${selectedVisionOption === index ? 'border-primary' : ''}`}
            style={{ cursor: 'pointer' }}
            onClick={() => selectVisionOption(index)}
          >
            <Card.Body>
              <Form.Check
                type="radio"
                name="vision"
                id={`vision-option-${index}`}
                checked={selectedVisionOption === index}
                onChange={() => selectVisionOption(index)}
                label={
                  <div>
                    <h5 className="mb-2">Option {index + 1}</h5>
                    <p className="mb-0">{option}</p>
                  </div>
                }
              />
            </Card.Body>
          </Card>
        ))}

        {selectedVisionOption !== null && (
          <Card className="mb-3 bg-light">
            <Card.Body>
              <Form.Label><strong>Customize (Optional):</strong></Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={editedVision}
                onChange={(e) => setEditedVision(e.target.value)}
              />
            </Card.Body>
          </Card>
        )}

        <div className="text-center mb-3">
          <Button
            variant="link"
            onClick={regenerateVision}
            disabled={loading}
          >
            🔄 Regenerate Options
          </Button>
        </div>

        <div className="d-flex justify-content-between">
          <Button
            variant="outline-secondary"
            onClick={() => navigate('/dashboard/foundation')}
          >
            Save for Later
          </Button>
          <Button
            variant="primary"
            onClick={saveVision}
            disabled={loading || selectedVisionOption === null}
          >
            {loading ? 'Saving...' : 'Save Vision Statement'}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );

  const renderComplete = () => (
    <Card className="shadow">
      <Card.Body className="p-4">
        <h2 className="mb-4">Refinement Complete</h2>

        <Alert variant="success">
          Your statements have been refined for your target market!
        </Alert>

        <Card className="mb-4 border-success">
          <Card.Body>
            <h5 className="mb-3">Refined for: {targetMarket}</h5>
            {editedMission && (
              <div className="mb-3">
                <strong>Mission:</strong>
                <p className="lead mb-0">{editedMission}</p>
              </div>
            )}
            {editedVision && (
              <div>
                <strong>Vision:</strong>
                <p className="lead mb-0">{editedVision}</p>
              </div>
            )}
          </Card.Body>
        </Card>

        <div className="d-flex justify-content-between">
          <Button variant="outline-secondary" onClick={() => navigate('/dashboard/foundation')}>
            Back to Dashboard
          </Button>
          <Button variant="primary" onClick={() => setStage('choice')}>
            Refine Again
          </Button>
        </div>
      </Card.Body>
    </Card>
  );

  return (
    <Container className="py-5">
      <BackToDashboard module="foundation" />
      <Row className="justify-content-center">
        <Col md={10} lg={8}>
          {stage === 'choice' && renderChoice()}
          {(stage === 'mission' || stage === 'both_mission') && renderMission()}
          {(stage === 'vision' || stage === 'both_vision') && renderVision()}
          {stage === 'complete' && renderComplete()}
        </Col>
      </Row>
    </Container>
  );
}

export default MissionVisionRefinement;
